import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, FormControl, Select, MenuItem, TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Disponibilidad from './Disponibilidad';
import DialogShell from '../components/design-system/DialogShell';
import FormSection from '../components/design-system/FormSection';
import { fieldSx, labelSx, multilineFieldSx, menuProps, estiloTabla, btnPrimary } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

const ESTADO_MEDICO = [
  { value: 'activo',     label: 'Activo',     color: 'success' },
  { value: 'inactivo',   label: 'Inactivo',   color: 'default' },
  { value: 'vacaciones', label: 'Vacaciones', color: 'warning' },
  { value: 'licencia',   label: 'Licencia',   color: 'error' },
];

const formVacio = {
  nombre: '', especialidad_ids: [], matricula: '', dni: '', cuit: '',
  email: '', celular: '', domicilio: '', descripcion: '', estado: 'activo', color: '#1565c0',
};

function LF({ label, children }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function Medicos() {
  const rol = sessionStorage.getItem('rol') || '';
  const [medicos, setMedicos]               = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [editando, setEditando]             = useState(null);
  const [form, setForm]                     = useState(formVacio);
  const [dispOpen, setDispOpen]             = useState(false);
  const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
  const [guardando, setGuardando]           = useState(false);
  const [error, setError]                   = useState('');

  const [usuarioOpen, setUsuarioOpen]           = useState(false);
  const [usuarioMedico, setUsuarioMedico]       = useState(null);
  const [usuarioExistente, setUsuarioExistente] = useState(null);
  const [usuarioForm, setUsuarioForm]           = useState({ nombre: '', email: '', password: '' });
  const [usuarioGuardando, setUsuarioGuardando] = useState(false);
  const [usuarioError, setUsuarioError]         = useState('');
  const [usuarioExito, setUsuarioExito]         = useState('');

  useEffect(() => { cargarTodo(); }, []);

  async function cargarTodo() {
    try {
      const [medicosR, espR] = await Promise.all([
        axios.get(`${API}/api/medicos`),
        axios.get(`${API}/api/especialidades`),
      ]);
      setMedicos(medicosR.data);
      setEspecialidades(espR.data);
    } catch (err) { console.error(err); }
  }

  function abrirNuevo() { setEditando(null); setForm(formVacio); setError(''); setDialogOpen(true); }
  function abrirEditar(m) {
    setEditando(m.id);
    setForm({
      nombre: m.nombre || '', especialidad_ids: (m.especialidades || []).map(e => e.id),
      matricula: m.matricula || '', dni: m.dni || '', cuit: m.cuit || '',
      email: m.email || '', celular: m.celular || '', domicilio: m.domicilio || '',
      descripcion: m.descripcion || '', estado: m.estado || 'activo', color: m.color || '#1565c0',
    });
    setError(''); setDialogOpen(true);
  }
  function cerrar() { setDialogOpen(false); }

  async function guardar() {
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setGuardando(true); setError('');
    try {
      if (editando) { await axios.put(`${API}/api/medicos/${editando}`, form); }
      else          { await axios.post(`${API}/api/medicos`, form); }
      cerrar(); cargarTodo();
    } catch (err) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  }

  async function abrirUsuario(m) {
    setUsuarioMedico(m); setUsuarioError(''); setUsuarioExito('');
    setUsuarioForm({ nombre: m.nombre, email: m.email || '', password: '' });
    setUsuarioExistente(null); setUsuarioOpen(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${m.id}/usuario`);
      setUsuarioExistente(r.data);
      if (r.data) setUsuarioForm({ nombre: r.data.nombre, email: r.data.email, password: '' });
    } catch (err) { console.error(err); }
  }

  async function guardarUsuario() {
    if (!usuarioForm.email.trim()) { setUsuarioError('El email es obligatorio'); return; }
    if (!usuarioExistente && !usuarioForm.password.trim()) { setUsuarioError('La contraseña es obligatoria'); return; }
    setUsuarioGuardando(true); setUsuarioError(''); setUsuarioExito('');
    try {
      if (usuarioExistente) {
        const payload = { nombre: usuarioForm.nombre, email: usuarioForm.email, rol: 'medico', medico_id: usuarioMedico.id };
        if (usuarioForm.password.trim()) payload.password = usuarioForm.password;
        await axios.put(`${API}/api/staff/${usuarioExistente.id}`, payload);
        setUsuarioExito('Usuario actualizado correctamente.');
      } else {
        await axios.post(`${API}/api/staff`, {
          nombre: usuarioForm.nombre, email: usuarioForm.email,
          password: usuarioForm.password, rol: 'medico', medico_id: usuarioMedico.id,
        });
        setUsuarioExito('Usuario creado correctamente.');
      }
      const r = await axios.get(`${API}/api/medicos/${usuarioMedico.id}/usuario`);
      setUsuarioExistente(r.data);
      setUsuarioForm(f => ({ ...f, password: '' }));
    } catch (err) { setUsuarioError(err.response?.data?.error || 'Error al guardar'); }
    finally { setUsuarioGuardando(false); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este médico?')) return;
    await axios.delete(`${API}/api/medicos/${id}`);
    cargarTodo();
  }

  function estadoChip(estado) {
    const e = ESTADO_MEDICO.find(x => x.value === estado) || ESTADO_MEDICO[0];
    return <Chip label={e.label} color={e.color} size="small" />;
  }

  const upd = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Gestión de Médicos
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo} sx={btnPrimary}>
          Nuevo Médico
        </Button>
      </Box>

      <TableContainer component={Paper} sx={estiloTabla}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
              <TableCell>Color</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Matrícula</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Celular</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {medicos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: '#b0b0b0' }}>No hay médicos registrados</TableCell>
              </TableRow>
            ) : medicos.map(m => (
              <TableRow key={m.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                <TableCell>
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: m.color }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {m.nombre}
                  {m.dni && <Typography sx={{ fontSize: 11, color: 'var(--c-acento)' }}>DNI: {m.dni}</Typography>}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{m.matricula || '—'}</TableCell>
                <TableCell>
                  {(m.especialidades || []).length
                    ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {m.especialidades.map(e => (
                          <Chip key={e.id} label={e.nombre} size="small" sx={{ bgcolor: 'var(--c-primario)', color: 'var(--c-acento)' }} />
                        ))}
                      </Box>
                    )
                    : <Typography sx={{ color: '#666', fontSize: 13 }}>—</Typography>}
                </TableCell>
                <TableCell>{m.email || '—'}</TableCell>
                <TableCell>{m.celular || '—'}</TableCell>
                <TableCell>{estadoChip(m.estado)}</TableCell>
                <TableCell>
                  <IconButton onClick={() => abrirEditar(m)} sx={{ color: 'var(--c-acento)' }} size="small" title="Editar">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {rol === 'admin' && (
                    <IconButton onClick={() => eliminar(m.id)} sx={{ color: '#f48fb1' }} size="small" title="Desactivar">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton onClick={() => { setMedicoSeleccionado(m); setDispOpen(true); }}
                    sx={{ color: '#81c784' }} size="small" title="Disponibilidad">
                    <ScheduleIcon fontSize="small" />
                  </IconButton>
                  {rol === 'admin' && (
                    <IconButton onClick={() => abrirUsuario(m)} sx={{ color: '#ce93d8' }} size="small" title="Gestionar usuario">
                      <ManageAccountsIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Dialog principal médico ── */}
      <DialogShell
        open={dialogOpen}
        onClose={cerrar}
        maxWidth="md"
        title={editando ? 'Editar Médico' : 'Nuevo Médico'}
        actions={
          <>
            <Button onClick={cerrar} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
            <Button variant="contained" onClick={guardar} disabled={guardando} sx={btnPrimary}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </>
        }
      >
        <FormSection title="Datos personales" first>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <LF label="Nombre completo *">
              <TextField fullWidth value={form.nombre} onChange={upd('nombre')} sx={fieldSx} />
            </LF>
            <LF label="Matrícula">
              <TextField fullWidth value={form.matricula} onChange={upd('matricula')} sx={fieldSx} />
            </LF>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <LF label="DNI">
              <TextField fullWidth value={form.dni} onChange={upd('dni')} sx={fieldSx} />
            </LF>
            <LF label="CUIT">
              <TextField fullWidth value={form.cuit} onChange={upd('cuit')} sx={fieldSx} />
            </LF>
          </Box>
        </FormSection>

        <FormSection title="Contacto">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <LF label="Email">
              <TextField type="email" fullWidth value={form.email} onChange={upd('email')} sx={fieldSx} />
            </LF>
            <LF label="Celular">
              <TextField fullWidth value={form.celular} onChange={upd('celular')} sx={fieldSx} />
            </LF>
          </Box>
          <LF label="Domicilio profesional">
            <TextField fullWidth value={form.domicilio} onChange={upd('domicilio')} sx={fieldSx} />
          </LF>
        </FormSection>

        <FormSection title="Configuración">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <LF label="Especialidades">
              <FormControl fullWidth sx={fieldSx}>
                <Select multiple displayEmpty value={form.especialidad_ids}
                  onChange={e => setForm(prev => ({ ...prev, especialidad_ids: e.target.value }))}
                  renderValue={(selected) => selected.length
                    ? especialidades.filter(e => selected.includes(e.id)).map(e => e.nombre).join(', ')
                    : <em style={{ color: '#888' }}>Sin especialidad</em>}
                  MenuProps={menuProps} sx={{ color: 'var(--c-texto)' }}>
                  {especialidades.map(e => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </LF>
            <LF label="Estado">
              <FormControl fullWidth sx={fieldSx}>
                <Select displayEmpty value={form.estado} onChange={upd('estado')}
                  MenuProps={menuProps} sx={{ color: 'var(--c-texto)' }}>
                  {ESTADO_MEDICO.map(e => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
                </Select>
              </FormControl>
            </LF>
          </Box>
          <LF label="Descripción / Presentación profesional">
            <TextField fullWidth multiline rows={3} value={form.descripcion} onChange={upd('descripcion')} sx={multilineFieldSx} />
          </LF>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ color: 'var(--c-acento)', fontSize: 13 }}>Color en agenda:</Typography>
            <select value={form.color} onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
              style={{ background: 'var(--c-superficie)', color: '#e0e0e0', border: '1px solid rgba(144,202,249,0.2)', borderRadius: 4, padding: '6px 10px' }}>
              <option value="#1565c0">Azul</option>
              <option value="#2e7d32">Verde</option>
              <option value="#ad1457">Rosa</option>
              <option value="#e65100">Naranja</option>
              <option value="#6a1b9a">Violeta</option>
              <option value="#00695c">Verde oscuro</option>
              <option value="#c62828">Rojo</option>
              <option value="#37474f">Gris</option>
              <option value="#f57f17">Amarillo</option>
              <option value="#0277bd">Celeste</option>
            </select>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: form.color, border: '2px solid rgba(255,255,255,0.15)' }} />
          </Box>
        </FormSection>

        {error && <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1.5 }}>{error}</Typography>}
      </DialogShell>

      {/* ── Dialog gestionar usuario del médico ── */}
      <DialogShell
        open={usuarioOpen}
        onClose={() => setUsuarioOpen(false)}
        maxWidth="xs"
        title={usuarioExistente ? 'Modificar usuario' : 'Crear usuario'}
        subtitle={usuarioMedico?.nombre}
        actions={
          <>
            <Button onClick={() => setUsuarioOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cerrar</Button>
            <Button variant="contained" onClick={guardarUsuario} disabled={usuarioGuardando} sx={btnPrimary}>
              {usuarioGuardando ? 'Guardando...' : usuarioExistente ? 'Guardar cambios' : 'Crear usuario'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {usuarioExistente ? (
            <Typography sx={{ fontSize: 12, color: '#66bb6a', bgcolor: 'rgba(102,187,106,0.1)', p: 1.5, borderRadius: 1, border: '1px solid #2e7d32' }}>
              ✓ Este médico ya tiene usuario activo
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#ffb74d', bgcolor: 'rgba(255,183,77,0.1)', p: 1.5, borderRadius: 1, border: '1px solid #e65100' }}>
              Este médico aún no tiene usuario para ingresar al sistema
            </Typography>
          )}
          <LF label="Nombre completo">
            <TextField fullWidth value={usuarioForm.nombre}
              onChange={e => setUsuarioForm(f => ({ ...f, nombre: e.target.value }))} sx={fieldSx} />
          </LF>
          <LF label="Email *">
            <TextField type="email" fullWidth value={usuarioForm.email}
              onChange={e => setUsuarioForm(f => ({ ...f, email: e.target.value }))} sx={fieldSx} />
          </LF>
          <LF label={usuarioExistente ? 'Nueva contraseña (vacío = sin cambios)' : 'Contraseña *'}>
            <TextField type="password" fullWidth value={usuarioForm.password}
              onChange={e => setUsuarioForm(f => ({ ...f, password: e.target.value }))} sx={fieldSx} />
          </LF>
          {usuarioError && <Typography sx={{ color: '#f44336', fontSize: 13 }}>{usuarioError}</Typography>}
          {usuarioExito && <Typography sx={{ color: '#66bb6a', fontSize: 13 }}>{usuarioExito}</Typography>}
        </Box>
      </DialogShell>

      {dispOpen && medicoSeleccionado && (
        <Disponibilidad
          medicoId={medicoSeleccionado.id}
          medicoNombre={medicoSeleccionado.nombre}
          onClose={() => { setDispOpen(false); setMedicoSeleccionado(null); }}
        />
      )}
    </Box>
  );
}
